import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ArrowLeft } from "lucide-react";

function GoogleButton({ label = "Continuar con Google" }) {
  const handleClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      data-testid="google-auth-btn"
      onClick={handleClick}
      type="button"
      className="w-full inline-flex items-center justify-center gap-3 py-3 rounded-full border border-rule bg-white hover:bg-paper-2 transition-colors text-[14px] font-medium text-ink"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.4-.3-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c3 0 5.7 1.1 7.7 2.9l5.7-5.7C33.8 6.6 29.1 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13-5.1l-6-5.1c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39 16.2 43.5 24 43.5z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.3 5.8l6 5.1c-.4.4 6.5-4.8 6.5-14.9 0-1.2-.1-2.4-.3-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name.split(" ")[0]}`);
      // Admin always lands on admin panel, otherwise honor "from" or default to dashboard
      const dest = user.role === "admin"
        ? "/admin"
        : (location.state?.from || "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No fue posible iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-paper">
      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft hover:text-forest mb-6 transition-colors"
          >
            <ArrowLeft size={13} /> Volver al inicio
          </Link>
          <Link to="/" data-testid="brand-link" className="inline-flex items-baseline gap-0.5">
            <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
          </Link>
          <h1 className="mt-10 font-serif text-3xl text-ink leading-tight">
            Bienvenido de nuevo
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            Continúa donde dejaste tu tesis.
          </p>

          <div className="mt-8">
            <GoogleButton label="Iniciar sesión con Google" />
          </div>

          <div className="my-7 flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-ink-soft/70">
            <span className="flex-1 h-px bg-rule" />
            o con tu correo
            <span className="flex-1 h-px bg-rule" />
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="login-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Correo</span>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tunombre@universidad.mx"
                className="px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Contraseña</span>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-4 py-3 rounded-lg border border-rule bg-white text-[14px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition pr-11"
                />
                <button
                  type="button"
                  aria-label="Mostrar contraseña"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="mt-2 group inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all disabled:opacity-60 shadow-sm hover:shadow-md hover:-translate-y-[1px]"
            >
              {submitting ? "Entrando…" : "Entrar a unaTESIS"}
              {!submitting && <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />}
            </button>
          </form>

          <p className="mt-8 text-sm text-ink-soft text-center">
            ¿No tienes cuenta?{" "}
            <Link to="/signup" data-testid="link-to-signup" className="text-forest font-medium hover:text-copper transition-colors">
              Crear una cuenta gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Editorial side */}
      <div className="hidden lg:flex items-center justify-center bg-[#F3F1EC] relative overflow-hidden">
        <div className="max-w-lg px-12">
          <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
            Para sustentantes
          </p>
          <p className="mt-6 font-serif italic text-3xl text-forest leading-snug">
            “Mi directora me felicitó por la solidez de mis citas. No le dije que unaTESIS las verificó una por una.”
          </p>
          <p className="mt-6 text-sm text-ink-soft">
            — Mariana C., Maestría en Educación · UNAM
          </p>
        </div>
        <div aria-hidden className="absolute -bottom-32 -right-32 h-[440px] w-[440px] rounded-full bg-[#E9E2D2]/60 blur-3xl" />
      </div>
    </div>
  );
}
